import { useState, useCallback, useEffect } from 'react';
import { communicationsAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  Phone, Plus, Trash2, Save, PhoneIncoming, Clock, Users,
  Voicemail, PhoneOff, ArrowRight, Check, Loader2, X,
  ChevronDown, ChevronUp, Music, PhoneForwarded, Menu,
  MessageSquare, GitBranch, AlertTriangle, Eye, Play,
} from 'lucide-react';
import { FlowPreviewPanel } from './FlowPreview';
import { FlowSimulator } from './FlowSimulator';

// ── Step type definitions ──
const STEP_TYPES = [
  { type: 'incoming', label: 'Incoming Call', icon: PhoneIncoming, color: 'bg-blue-100 text-blue-700', canAdd: false },
  { type: 'greeting', label: 'Greeting', icon: MessageSquare, color: 'bg-teal-100 text-teal-700', canAdd: true },
  { type: 'call_hours', label: 'Call Hours', icon: Clock, color: 'bg-amber-100 text-amber-700', canAdd: true },
  { type: 'menu', label: 'IVR Menu', icon: Menu, color: 'bg-indigo-100 text-indigo-700', canAdd: true },
  { type: 'simul_ring', label: 'Simul-Ring', icon: Users, color: 'bg-green-100 text-green-700', canAdd: true },
  { type: 'forward', label: 'Forward Call', icon: PhoneForwarded, color: 'bg-cyan-100 text-cyan-700', canAdd: true },
  { type: 'voicemail', label: 'Voicemail', icon: Voicemail, color: 'bg-purple-100 text-purple-700', canAdd: true },
  { type: 'end_call', label: 'End Call', icon: PhoneOff, color: 'bg-red-100 text-red-700', canAdd: true },
];

const ADDABLE_TYPES = STEP_TYPES.filter(s => s.canAdd);

function getMeta(type) {
  return STEP_TYPES.find(s => s.type === type) || STEP_TYPES[STEP_TYPES.length - 1];
}

// ── Default configs for new steps ──
function defaultConfig(type) {
  switch (type) {
    case 'greeting': return { message: 'Thank you for calling. Please hold while we connect you.' };
    case 'call_hours': return { mode: '24_7' };
    case 'menu': return { prompt_text: 'Press 1 for Sales. Press 2 for Support. Press 0 for the operator.', timeout: 5, max_attempts: 3 };
    case 'simul_ring': return { target: 'all_web_phones', timeout: 25 };
    case 'forward': return { number: '', timeout: 30 };
    case 'voicemail': return { message: "We're sorry, no one is available. Please leave a message after the beep." };
    case 'end_call': return {};
    default: return {};
  }
}

function defaultBranches(type) {
  switch (type) {
    case 'menu': return {
      '1': [{ type: 'simul_ring', config: { target: 'all_web_phones', timeout: 25 } }],
      '2': [{ type: 'simul_ring', config: { target: 'all_web_phones', timeout: 25 } }],
      timeout: [{ type: 'voicemail', config: { message: 'No selection was made. Please leave a message.' } }],
    };
    case 'call_hours': return {
      open: [{ type: 'simul_ring', config: { target: 'all_web_phones', timeout: 25 } }],
      closed: [{ type: 'voicemail', config: { message: 'We are currently closed. Please leave a message.' } }],
    };
    default: return undefined;
  }
}

// ── Validation ──
function validateFlow(steps) {
  const errors = [];

  function validateStepList(stepList, path) {
    for (let i = 0; i < stepList.length; i++) {
      const step = stepList[i];
      const loc = path ? `${path} > Step ${i + 1}` : `Step ${i + 1}`;

      if (step.type === 'menu') {
        const branches = step.branches || {};
        const digitKeys = Object.keys(branches).filter(k => k !== 'timeout' && k !== 'invalid');
        if (digitKeys.length === 0) {
          errors.push(`${loc} (IVR Menu): Must have at least one digit branch.`);
        }
        if (!step.config?.prompt_text && !step.config?.audio_file_id) {
          errors.push(`${loc} (IVR Menu): Must have a prompt (text or audio file).`);
        }
        for (const key of Object.keys(branches)) {
          const branchSteps = branches[key];
          if (!branchSteps || branchSteps.length === 0) {
            const label = key === 'timeout' ? 'Timeout' : key === 'invalid' ? 'Invalid' : `Digit ${key}`;
            errors.push(`${loc} (IVR Menu): Branch "${label}" has no actions.`);
          } else {
            const branchLabel = key === 'timeout' ? 'Timeout' : key === 'invalid' ? 'Invalid' : `Digit ${key}`;
            validateStepList(branchSteps, `${loc} > ${branchLabel}`);
          }
        }
      }

      if (step.type === 'call_hours' && step.branches) {
        const branches = step.branches;
        for (const bk of ['open', 'closed']) {
          const branchSteps = branches[bk] || [];
          if (branchSteps.length === 0) {
            errors.push(`${loc} (Call Hours): "${bk === 'open' ? 'Open' : 'Closed'}" branch has no actions.`);
          } else {
            validateStepList(branchSteps, `${loc} > ${bk === 'open' ? 'Open Hours' : 'Closed Hours'}`);
          }
        }
      }

      if (step.type === 'forward') {
        const num = (step.config?.number || '').trim();
        if (!num) {
          errors.push(`${loc} (Forward): Phone number is required.`);
        } else if (!/^\+?\d{7,15}$/.test(num.replace(/[\s\-()]/g, ''))) {
          errors.push(`${loc} (Forward): Phone number appears invalid.`);
        }
      }

      if (step.type === 'greeting') {
        if (!step.config?.message && !step.config?.audio_file_id) {
          errors.push(`${loc} (Greeting): Must have a message or audio file.`);
        }
      }
    }
  }

  validateStepList(steps, '');
  return errors;
}


// ══════════════════════════════════════════════════════════════
// AudioOrTTSPicker — reusable TTS/Audio toggle + selector
// ══════════════════════════════════════════════════════════════

function AudioOrTTSPicker({ config, audioFiles, onChange, textPlaceholder, testIdPrefix }) {
  const isAudioMode = config.audio_file_id !== undefined;

  return (
    <div className="space-y-2">
      <div className="flex gap-1.5">
        <button
          onClick={() => onChange({ audio_file_id: undefined })}
          className={`text-[11px] px-2 py-1 rounded transition-colors ${!isAudioMode ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
          data-testid={`${testIdPrefix}-mode-tts`}
        >Text-to-Speech</button>
        <button
          onClick={() => onChange({ audio_file_id: '' })}
          className={`text-[11px] px-2 py-1 rounded transition-colors ${isAudioMode ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
          data-testid={`${testIdPrefix}-mode-audio`}
        >Audio File</button>
      </div>
      {!isAudioMode ? (
        <Textarea
          value={config.message || config.prompt_text || ''}
          onChange={e => onChange({ [config.prompt_text !== undefined ? 'prompt_text' : 'message']: e.target.value })}
          rows={2}
          className="text-xs"
          placeholder={textPlaceholder}
          data-testid={`${testIdPrefix}-text`}
        />
      ) : (
        <div className="space-y-1.5">
          <select
            value={config.audio_file_id || ''}
            onChange={e => onChange({ audio_file_id: e.target.value || undefined })}
            className="w-full text-xs px-2 py-1.5 rounded border bg-background"
            data-testid={`${testIdPrefix}-audio-select`}
          >
            <option value="">Select audio file...</option>
            {(audioFiles || []).map(af => (
              <option key={af.id} value={af.id}>{af.title}</option>
            ))}
          </select>
          {config.audio_file_id && (
            <audio
              src={communicationsAPI.audioFileUrl(config.audio_file_id)}
              controls
              preload="none"
              className="w-full h-8"
              data-testid={`${testIdPrefix}-audio-preview`}
            />
          )}
          {(!audioFiles || audioFiles.length === 0) && (
            <p className="text-[10px] text-muted-foreground">No audio files uploaded yet. Upload files in the Audio Library section.</p>
          )}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// StepEditor — renders config for a single step
// ══════════════════════════════════════════════════════════════

function StepEditor({ step, onChange, audioFiles, ringGroups, employees, depth }) {
  const { type, config } = step;

  if (type === 'incoming' || type === 'end_call') return null;

  if (type === 'greeting') {
    return (
      <AudioOrTTSPicker
        config={config}
        audioFiles={audioFiles}
        onChange={updates => onChange({ config: { ...config, ...updates } })}
        textPlaceholder="Greeting message to play before routing..."
        testIdPrefix="greeting"
      />
    );
  }

  if (type === 'call_hours') {
    return (
      <div className="space-y-1">
        <select
          value={config.mode || '24_7'}
          onChange={e => onChange({ config: { ...config, mode: e.target.value } })}
          className="text-xs px-2 py-1.5 rounded border bg-background w-full sm:w-auto"
          data-testid="hours-mode-select"
        >
          <option value="24_7">24/7 - Always available</option>
          <option value="business_hours">Company business hours</option>
          <option value="custom">Custom hours</option>
        </select>
        {config.mode !== '24_7' && !step.branches && (
          <p className="text-[10px] text-muted-foreground">This step uses legacy linear mode. Enable branching to configure separate Open/Closed paths.</p>
        )}
      </div>
    );
  }

  if (type === 'menu') {
    return (
      <div className="space-y-2">
        <AudioOrTTSPicker
          config={{ ...config, prompt_text: config.prompt_text }}
          audioFiles={audioFiles}
          onChange={updates => {
            const next = { ...config };
            if ('prompt_text' in updates) next.prompt_text = updates.prompt_text;
            if ('audio_file_id' in updates) next.audio_file_id = updates.audio_file_id;
            if ('message' in updates) next.prompt_text = updates.message;
            onChange({ config: next });
          }}
          textPlaceholder="Press 1 for Sales. Press 2 for Support..."
          testIdPrefix="menu-prompt"
        />
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-muted-foreground">Timeout:</span>
            <Input
              type="number"
              min={1}
              max={30}
              value={config.timeout || 5}
              onChange={e => onChange({ config: { ...config, timeout: parseInt(e.target.value) || 5 } })}
              className="w-14 h-7 text-xs"
              data-testid="menu-timeout"
            />
            <span className="text-[11px] text-muted-foreground">sec</span>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'simul_ring') {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <select
          value={config.target || 'all_web_phones'}
          onChange={e => onChange({ config: { ...config, target: e.target.value } })}
          className="text-xs px-2 py-1.5 rounded border bg-background w-full sm:w-auto"
          data-testid="ring-target-select"
        >
          <option value="all_web_phones">All web phones</option>
          <option value="ring_group">Ring group</option>
          <option value="employee">Specific employee</option>
        </select>
        {config.target === 'ring_group' && (
          <select
            value={config.ring_group_id || ''}
            onChange={e => onChange({ config: { ...config, ring_group_id: e.target.value } })}
            className="text-xs px-2 py-1.5 rounded border bg-background w-full sm:w-auto"
            data-testid="ring-group-select"
          >
            <option value="">Select group...</option>
            {(ringGroups || []).map(rg => <option key={rg.id} value={rg.id}>{rg.name}</option>)}
          </select>
        )}
        {config.target === 'employee' && (
          <select
            value={config.user_id || ''}
            onChange={e => onChange({ config: { ...config, user_id: e.target.value } })}
            className="text-xs px-2 py-1.5 rounded border bg-background w-full sm:w-auto"
            data-testid="ring-employee-select"
          >
            <option value="">Select employee...</option>
            {(employees || []).map(emp => <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>)}
          </select>
        )}
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Timeout:</span>
          <Input
            type="number"
            min={5}
            max={120}
            value={config.timeout || 25}
            onChange={e => onChange({ config: { ...config, timeout: parseInt(e.target.value) || 25 } })}
            className="w-16 h-7 text-xs"
            data-testid="ring-timeout-input"
          />
          <span className="text-[11px] text-muted-foreground">sec</span>
        </div>
      </div>
    );
  }

  if (type === 'forward') {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <Input
          value={config.number || ''}
          onChange={e => onChange({ config: { ...config, number: e.target.value } })}
          placeholder="+1 (555) 123-4567"
          className="text-xs h-8 w-full sm:max-w-[200px]"
          data-testid="forward-number-input"
        />
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-muted-foreground">Timeout:</span>
          <Input
            type="number"
            min={5}
            max={120}
            value={config.timeout || 30}
            onChange={e => onChange({ config: { ...config, timeout: parseInt(e.target.value) || 30 } })}
            className="w-16 h-7 text-xs"
            data-testid="forward-timeout-input"
          />
          <span className="text-[11px] text-muted-foreground">sec</span>
        </div>
      </div>
    );
  }

  if (type === 'voicemail') {
    return (
      <AudioOrTTSPicker
        config={config}
        audioFiles={audioFiles}
        onChange={updates => onChange({ config: { ...config, ...updates } })}
        textPlaceholder="Voicemail greeting message..."
        testIdPrefix="voicemail"
      />
    );
  }

  return null;
}


// ══════════════════════════════════════════════════════════════
// BranchEditor — renders a named branch with nested steps
// ══════════════════════════════════════════════════════════════

function BranchEditor({ label, branchKey, steps, onUpdate, audioFiles, ringGroups, employees, depth }) {
  const [collapsed, setCollapsed] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const updateBranchStep = (idx, updates) => {
    const next = [...steps];
    next[idx] = { ...next[idx], ...updates };
    onUpdate(next);
  };

  const removeBranchStep = (idx) => {
    onUpdate(steps.filter((_, i) => i !== idx));
  };

  const addBranchStep = (type) => {
    const newStep = { type, config: defaultConfig(type) };
    if (type === 'menu') newStep.branches = defaultBranches('menu');
    onUpdate([...steps, newStep]);
    setShowAddMenu(false);
  };

  const moveBranchStep = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= steps.length) return;
    const next = [...steps];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onUpdate(next);
  };

  return (
    <div
      className="border-l-2 border-dashed pl-3 sm:pl-4 mt-2 space-y-2"
      style={{ borderColor: branchKey === 'timeout' || branchKey === 'closed' || branchKey === 'invalid' ? '#f59e0b' : '#6366f1' }}
      data-testid={`branch-${branchKey}`}
    >
      <div className="flex items-center justify-between">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          data-testid={`branch-toggle-${branchKey}`}
        >
          {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
          <GitBranch className="w-3 h-3" />
          {label}
          <Badge variant="outline" className="text-[9px] ml-1">{steps.length} step{steps.length !== 1 ? 's' : ''}</Badge>
        </button>
      </div>

      {!collapsed && (
        <div className="space-y-2">
          {steps.map((step, idx) => (
            <StepCard
              key={idx}
              step={step}
              idx={idx}
              total={steps.length}
              onChange={(updates) => updateBranchStep(idx, updates)}
              onRemove={() => removeBranchStep(idx)}
              onMove={(dir) => moveBranchStep(idx, dir)}
              audioFiles={audioFiles}
              ringGroups={ringGroups}
              employees={employees}
              depth={depth + 1}
            />
          ))}

          {/* Add step button */}
          <div className="relative">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px] gap-1"
              onClick={() => setShowAddMenu(!showAddMenu)}
              data-testid={`add-step-branch-${branchKey}`}
            >
              <Plus className="w-3 h-3" /> Add Step
            </Button>
            {showAddMenu && (
              <div className="absolute left-0 top-8 z-20 bg-background border rounded-lg shadow-lg p-1 min-w-[160px]" data-testid={`add-menu-${branchKey}`}>
                {ADDABLE_TYPES.map(st => {
                  const Icon = st.icon;
                  return (
                    <button
                      key={st.type}
                      onClick={() => addBranchStep(st.type)}
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs rounded hover:bg-muted transition-colors text-left"
                      data-testid={`add-${st.type}-to-${branchKey}`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      {st.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// MenuBranches — branch manager for IVR menu steps
// ══════════════════════════════════════════════════════════════

function MenuBranches({ step, onChange, audioFiles, ringGroups, employees, depth }) {
  const branches = step.branches || {};
  const [newDigit, setNewDigit] = useState('');
  const [showDigitInput, setShowDigitInput] = useState(false);

  const digitKeys = Object.keys(branches).filter(k => k !== 'timeout' && k !== 'invalid').sort();
  const hasTimeout = 'timeout' in branches;

  const updateBranch = (key, newSteps) => {
    onChange({ branches: { ...branches, [key]: newSteps } });
  };

  const addDigitBranch = () => {
    const d = newDigit.trim();
    if (!d || branches[d]) {
      toast.error(branches[d] ? `Branch for digit "${d}" already exists.` : 'Enter a digit.');
      return;
    }
    if (!/^[0-9*#]$/.test(d)) {
      toast.error('Enter a single digit (0-9), *, or #.');
      return;
    }
    onChange({
      branches: {
        ...branches,
        [d]: [{ type: 'simul_ring', config: { target: 'all_web_phones', timeout: 25 } }],
      },
    });
    setNewDigit('');
    setShowDigitInput(false);
  };

  const removeBranch = (key) => {
    const next = { ...branches };
    delete next[key];
    onChange({ branches: next });
  };

  const toggleTimeout = () => {
    if (hasTimeout) {
      const next = { ...branches };
      delete next.timeout;
      onChange({ branches: next });
    } else {
      onChange({
        branches: {
          ...branches,
          timeout: [{ type: 'voicemail', config: { message: 'No selection received. Please leave a message.' } }],
        },
      });
    }
  };

  return (
    <div className="space-y-2 mt-2" data-testid="menu-branches">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Branches</p>
        <div className="flex items-center gap-1.5">
          <button
            onClick={toggleTimeout}
            className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${hasTimeout ? 'bg-amber-50 border-amber-200 text-amber-700' : 'border-dashed text-muted-foreground hover:text-foreground'}`}
            data-testid="toggle-timeout-branch"
          >
            {hasTimeout ? 'Timeout' : '+ Timeout'}
          </button>
          <button
            onClick={() => setShowDigitInput(!showDigitInput)}
            className="text-[10px] px-2 py-0.5 rounded border border-dashed text-muted-foreground hover:text-foreground transition-colors"
            data-testid="add-digit-branch-btn"
          >
            + Digit
          </button>
        </div>
      </div>

      {showDigitInput && (
        <div className="flex items-center gap-2 bg-muted/50 rounded-md p-2" data-testid="new-digit-input">
          <Input
            value={newDigit}
            onChange={e => setNewDigit(e.target.value.slice(-1))}
            placeholder="0-9, *, #"
            className="w-16 h-7 text-xs text-center"
            maxLength={1}
            data-testid="digit-key-input"
          />
          <Button size="sm" className="h-7 text-[11px]" onClick={addDigitBranch} data-testid="confirm-add-digit">
            <Check className="w-3 h-3 mr-1" /> Add
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => { setShowDigitInput(false); setNewDigit(''); }}>
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Digit branches */}
      {digitKeys.map(dk => (
        <div key={dk} className="relative">
          <div className="absolute right-0 top-0 z-10">
            <button
              onClick={() => removeBranch(dk)}
              className="text-red-400 hover:text-red-600 p-0.5 transition-colors"
              title={`Remove digit ${dk} branch`}
              data-testid={`remove-branch-${dk}`}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <BranchEditor
            label={`Press ${dk}`}
            branchKey={dk}
            steps={branches[dk] || []}
            onUpdate={(newSteps) => updateBranch(dk, newSteps)}
            audioFiles={audioFiles}
            ringGroups={ringGroups}
            employees={employees}
            depth={depth}
          />
        </div>
      ))}

      {/* Timeout branch */}
      {hasTimeout && (
        <div className="relative">
          <div className="absolute right-0 top-0 z-10">
            <button
              onClick={toggleTimeout}
              className="text-red-400 hover:text-red-600 p-0.5 transition-colors"
              title="Remove timeout branch"
              data-testid="remove-timeout-branch"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
          <BranchEditor
            label="No Input (Timeout)"
            branchKey="timeout"
            steps={branches.timeout || []}
            onUpdate={(newSteps) => updateBranch('timeout', newSteps)}
            audioFiles={audioFiles}
            ringGroups={ringGroups}
            employees={employees}
            depth={depth}
          />
        </div>
      )}

      {digitKeys.length === 0 && !hasTimeout && (
        <p className="text-[10px] text-muted-foreground italic py-1">No branches configured. Add digit branches for callers to press.</p>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// CallHoursBranches — open/closed branch manager
// ══════════════════════════════════════════════════════════════

function CallHoursBranches({ step, onChange, audioFiles, ringGroups, employees, depth }) {
  const branches = step.branches || {};
  const hasBranches = 'open' in branches || 'closed' in branches;

  const enableBranching = () => {
    onChange({
      branches: {
        open: [{ type: 'simul_ring', config: { target: 'all_web_phones', timeout: 25 } }],
        closed: [{ type: 'voicemail', config: { message: 'We are currently closed. Please leave a message.' } }],
      },
    });
  };

  const disableBranching = () => {
    onChange({ branches: undefined });
  };

  const updateBranch = (key, newSteps) => {
    onChange({ branches: { ...branches, [key]: newSteps } });
  };

  if (!hasBranches) {
    return (
      <div className="mt-2">
        {step.config?.mode !== '24_7' && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px] gap-1"
            onClick={enableBranching}
            data-testid="enable-hours-branching"
          >
            <GitBranch className="w-3 h-3" /> Enable Open/Closed Branching
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 mt-2" data-testid="hours-branches">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Hour Branches</p>
        <button
          onClick={disableBranching}
          className="text-[10px] text-red-500 hover:text-red-700 transition-colors"
          data-testid="disable-hours-branching"
        >
          Revert to Linear
        </button>
      </div>
      <BranchEditor
        label="Open Hours"
        branchKey="open"
        steps={branches.open || []}
        onUpdate={(newSteps) => updateBranch('open', newSteps)}
        audioFiles={audioFiles}
        ringGroups={ringGroups}
        employees={employees}
        depth={depth}
      />
      <BranchEditor
        label="Closed Hours"
        branchKey="closed"
        steps={branches.closed || []}
        onUpdate={(newSteps) => updateBranch('closed', newSteps)}
        audioFiles={audioFiles}
        ringGroups={ringGroups}
        employees={employees}
        depth={depth}
      />
    </div>
  );
}


// ══════════════════════════════════════════════════════════════
// StepCard — single step card with expand/collapse, config, branches
// ══════════════════════════════════════════════════════════════

function StepCard({ step, idx, total, onChange, onRemove, onMove, audioFiles, ringGroups, employees, depth = 0 }) {
  const [expanded, setExpanded] = useState(depth === 0);
  const meta = getMeta(step.type);
  const Icon = meta.icon;
  const isFixed = step.type === 'incoming';
  const hasBranches = step.type === 'menu' || (step.type === 'call_hours' && step.branches);
  const stepSummary = getStepSummary(step);

  return (
    <Card className={`${depth > 0 ? 'border-dashed' : ''}`} data-testid={`flow-step-${step.type}${depth > 0 ? `-d${depth}` : ''}`}>
      <CardContent className="py-2.5 px-3 sm:px-4">
        {/* Header row */}
        <div className="flex items-center gap-2 sm:gap-3">
          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0 ${meta.color}`}>
            <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 w-full text-left"
              data-testid={`step-toggle-${idx}`}
            >
              <p className="text-sm font-medium truncate">{meta.label}</p>
              {stepSummary && !expanded && (
                <span className="text-[10px] text-muted-foreground truncate hidden sm:inline">— {stepSummary}</span>
              )}
              {(step.type !== 'incoming' && step.type !== 'end_call') && (
                expanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              )}
            </button>
          </div>
          {!isFixed && (
            <div className="flex items-center gap-0.5 shrink-0">
              {idx > 0 && onMove && (
                <button onClick={() => onMove(-1)} className="p-1 text-muted-foreground hover:text-foreground" data-testid={`move-up-${idx}`}>
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
              )}
              {idx < total - 1 && onMove && (
                <button onClick={() => onMove(1)} className="p-1 text-muted-foreground hover:text-foreground" data-testid={`move-down-${idx}`}>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              )}
              {onRemove && (
                <button onClick={onRemove} className="p-1 text-red-400 hover:text-red-600" data-testid={`remove-step-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Expanded config */}
        {expanded && (
          <div className="mt-2 pl-9 sm:pl-11">
            <StepEditor
              step={step}
              onChange={onChange}
              audioFiles={audioFiles}
              ringGroups={ringGroups}
              employees={employees}
              depth={depth}
            />

            {/* Branching UI */}
            {step.type === 'menu' && (
              <MenuBranches
                step={step}
                onChange={onChange}
                audioFiles={audioFiles}
                ringGroups={ringGroups}
                employees={employees}
                depth={depth}
              />
            )}
            {step.type === 'call_hours' && (
              <CallHoursBranches
                step={step}
                onChange={onChange}
                audioFiles={audioFiles}
                ringGroups={ringGroups}
                employees={employees}
                depth={depth}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function getStepSummary(step) {
  const c = step.config || {};
  switch (step.type) {
    case 'greeting': return c.audio_file_id ? 'Audio file' : (c.message || '').slice(0, 40);
    case 'call_hours': return c.mode === '24_7' ? '24/7' : c.mode === 'business_hours' ? 'Business hours' : 'Custom hours';
    case 'menu': {
      const branches = step.branches || {};
      const digits = Object.keys(branches).filter(k => k !== 'timeout' && k !== 'invalid');
      return digits.length > 0 ? `Digits: ${digits.join(', ')}` : 'No branches';
    }
    case 'simul_ring': return c.target === 'ring_group' ? 'Ring group' : c.target === 'employee' ? 'Employee' : 'All phones';
    case 'forward': return c.number || 'No number';
    case 'voicemail': return c.audio_file_id ? 'Audio file' : (c.message || '').slice(0, 40);
    default: return '';
  }
}


// ══════════════════════════════════════════════════════════════
// IVRFlowBuilder — main exported component
// ══════════════════════════════════════════════════════════════

export function IVRFlowBuilder({ flow, numbers, ringGroups, audioFiles, onSave, onCancel }) {
  const [name, setName] = useState(flow?.name || 'New Call Flow');
  const [steps, setSteps] = useState(() => {
    const initial = flow?.steps_json || [
      { type: 'incoming', config: {} },
      { type: 'call_hours', config: { mode: '24_7' } },
      { type: 'simul_ring', config: { target: 'all_web_phones', timeout: 25 } },
      { type: 'voicemail', config: { message: "We're sorry, no one is available. Please leave a message after the beep." } },
      { type: 'end_call', config: {} },
    ];
    return initial;
  });
  const [assignedNumbers, setAssignedNumbers] = useState(flow?.assigned_number_ids || []);
  const [saving, setSaving] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showSim, setShowSim] = useState(false);

  // Load employees for the employee ring target
  useEffect(() => {
    usersAPI.list({ include_archived: false }).then(res => {
      setEmployees(res.data || []);
    }).catch(() => {});
  }, []);

  const updateStep = useCallback((idx, updates) => {
    setSteps(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...updates };
      // Propagate config updates
      if (updates.config) next[idx].config = updates.config;
      if ('branches' in updates) next[idx].branches = updates.branches;
      return next;
    });
    setValidationErrors([]);
  }, []);

  const removeStep = useCallback((idx) => {
    setSteps(prev => prev.filter((_, i) => i !== idx));
    setValidationErrors([]);
  }, []);

  const addStep = useCallback((type) => {
    const newStep = { type, config: defaultConfig(type) };
    if (type === 'menu') newStep.branches = defaultBranches('menu');
    if (type === 'call_hours') {
      // Default: add branches for non-24/7 modes
      newStep.branches = defaultBranches('call_hours');
    }
    // Insert before end_call if it exists
    setSteps(prev => {
      const endIdx = prev.findIndex(s => s.type === 'end_call');
      if (endIdx >= 0) {
        const next = [...prev];
        next.splice(endIdx, 0, newStep);
        return next;
      }
      return [...prev, newStep];
    });
    setShowAddMenu(false);
    setValidationErrors([]);
  }, []);

  const moveStep = useCallback((idx, dir) => {
    setSteps(prev => {
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      // Don't allow moving past incoming or end_call anchors
      if (prev[newIdx].type === 'incoming' || prev[idx].type === 'incoming') return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  }, []);

  const handleSave = async () => {
    // Validate
    const errors = validateFlow(steps);
    if (errors.length > 0) {
      setValidationErrors(errors);
      toast.error(`${errors.length} validation error${errors.length > 1 ? 's' : ''} found. Fix them before saving.`);
      return;
    }

    setSaving(true);
    try {
      await onSave({ name, steps_json: steps, is_default: true }, assignedNumbers);
    } finally {
      setSaving(false);
    }
  };

  // Count steps recursively for display
  const totalStepCount = countSteps(steps);

  return (
    <div className="space-y-4" data-testid="call-flow-builder">
      {/* Flow name + preview toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          className="max-w-full sm:max-w-xs text-sm font-medium"
          data-testid="flow-name-input"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-[10px] w-fit">{totalStepCount} total steps</Badge>
          <Button
            size="sm"
            variant={showPreview ? 'default' : 'outline'}
            className="h-7 text-[11px] gap-1"
            onClick={() => { setShowPreview(!showPreview); if (!showPreview) setShowSim(false); }}
            data-testid="builder-preview-toggle"
          >
            <Eye className="w-3 h-3" /> {showPreview ? 'Hide Preview' : 'Preview'}
          </Button>
          <Button
            size="sm"
            variant={showSim ? 'default' : 'outline'}
            className="h-7 text-[11px] gap-1"
            onClick={() => { setShowSim(!showSim); if (!showSim) setShowPreview(false); }}
            data-testid="builder-sim-toggle"
          >
            <Play className="w-3 h-3" /> {showSim ? 'Hide Simulator' : 'Simulate'}
          </Button>
        </div>
      </div>

      {/* Inline preview panel */}
      {showPreview && (
        <Card>
          <CardContent className="py-4">
            <FlowPreviewPanel
              flow={{ name, steps_json: steps, assigned_number_ids: assignedNumbers, is_default: true }}
              onClose={() => setShowPreview(false)}
            />
          </CardContent>
        </Card>
      )}

      {/* Inline simulator panel */}
      {showSim && (
        <Card>
          <CardContent className="py-4" style={{ minHeight: '300px' }}>
            <FlowSimulator
              flow={{ name, steps_json: steps, assigned_number_ids: assignedNumbers }}
              onClose={() => setShowSim(false)}
            />
          </CardContent>
        </Card>
      )}

      {/* Assigned numbers */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-muted-foreground uppercase tracking-wide">Assigned Phone Numbers</CardTitle>
        </CardHeader>
        <CardContent>
          {numbers.length === 0 ? (
            <p className="text-xs text-muted-foreground">No numbers available. Get numbers first.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {numbers.map(n => {
                const active = assignedNumbers.includes(n.id);
                return (
                  <button
                    key={n.id}
                    onClick={() => setAssignedNumbers(prev => active ? prev.filter(x => x !== n.id) : [...prev, n.id])}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-background border-border hover:border-blue-300'}`}
                    data-testid={`assign-number-${n.id}`}
                  >
                    {n.phone_number}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1" data-testid="validation-errors">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
            <p className="text-xs font-medium text-red-800">Fix these issues before saving:</p>
          </div>
          <ul className="space-y-0.5 pl-5">
            {validationErrors.map((err, i) => (
              <li key={i} className="text-[11px] text-red-700 list-disc">{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Flow steps */}
      <div className="space-y-2">
        {steps.map((step, idx) => (
          <StepCard
            key={`${step.type}-${idx}`}
            step={step}
            idx={idx}
            total={steps.length}
            onChange={(updates) => updateStep(idx, updates)}
            onRemove={step.type === 'incoming' ? undefined : () => removeStep(idx)}
            onMove={(dir) => moveStep(idx, dir)}
            audioFiles={audioFiles}
            ringGroups={ringGroups}
            employees={employees}
            depth={0}
          />
        ))}
      </div>

      {/* Add step */}
      <div className="relative">
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={() => setShowAddMenu(!showAddMenu)}
          data-testid="add-step-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add Step
        </Button>
        {showAddMenu && (
          <div className="absolute left-0 bottom-10 z-30 bg-background border rounded-lg shadow-lg p-1 min-w-[180px]" data-testid="add-step-menu">
            {ADDABLE_TYPES.map(st => {
              const Icon = st.icon;
              return (
                <button
                  key={st.type}
                  onClick={() => addStep(st.type)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs rounded hover:bg-muted transition-colors text-left"
                  data-testid={`add-step-${st.type}`}
                >
                  <div className={`w-6 h-6 rounded flex items-center justify-center ${st.color}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  {st.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Save / Cancel */}
      <div className="flex gap-2 pt-2">
        <Button onClick={handleSave} disabled={saving} data-testid="save-flow-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save Call Flow
        </Button>
        {onCancel && <Button variant="ghost" onClick={onCancel} data-testid="cancel-flow-btn">Cancel</Button>}
      </div>
    </div>
  );
}

function countSteps(steps) {
  let count = 0;
  for (const step of steps) {
    count++;
    if (step.branches) {
      for (const key of Object.keys(step.branches)) {
        count += countSteps(step.branches[key] || []);
      }
    }
  }
  return count;
}
